<script lang="ts" setup>
  import { computed, ref } from "vue";
  import { useAppStore } from "@/stores/app";
  import { useSessionStore } from "@/stores/session";
  import MembershipTierDetails from "@/components/membership/MembershipTierDetails.vue";
  import Slider from "@/components/Slider.vue";
  import { useDisplay } from "vuetify";
  import { MembershipTierPlanFrequency } from "@/models/membershipTierPlan";
  import { useSubscriptionFlowStore } from "@/stores/subscriptionFlow";
  import * as amplitude from '@amplitude/analytics-browser'

  const props = defineProps<{
    showSubTitle?: boolean;
  }>();

  const sessionStore = useSessionStore();
  const appStore = useAppStore();
  const subscriptionFlowStore = useSubscriptionFlowStore();
  const resetPageTrigger = ref(false);


  const { mobile } = useDisplay();

  const selectedMembershipTier = computed(() => {
    return appStore.membershipTiers.find(o => o.id === sessionStore.membership?.membershipTierId);
  });
  const selectedMembershipTierPlan = computed(() => {
    return selectedMembershipTier.value?.membershipTierPlans.find(o => o.id === sessionStore.membership?.membershipTierPlanId);
  });
  const visibleTiers = computed(() => {
    return appStore.membershipTiers.filter((tier) => {
      return tier.id === selectedMembershipTier.value?.id || (!tier.hidden && tier.price !== selectedMembershipTier.value?.price);
    });
  });

  function toggleFrequency(showAnnual: boolean) {
    amplitude.track('Tier Cycle Switch Clicked', {
      "Tier Cycle": showAnnual ? MembershipTierPlanFrequency.YEAR : MembershipTierPlanFrequency.MONTH,
    });

    resetPageTrigger.value = !resetPageTrigger.value;
  }

  const firstRoundUpTier = computed(() => {
    return visibleTiers.value.find((tier) => !tier.isFixedTier);
  });

  const showAnnual = ref<boolean>(selectedMembershipTierPlan.value?.frequency === MembershipTierPlanFrequency.YEAR);
  const showAnnualToggle = computed(() => {
    return appStore.membershipTiers.filter((tier) => tier.membershipTierPlans.some((plan) => plan.frequency === MembershipTierPlanFrequency.YEAR)).length > 0;
  });
  const annualMaxSavings = computed(() => {
    return visibleTiers.value.reduce((savings, tier) => {
      const monthlyPlan = tier.membershipTierPlans.find((plan) => plan.frequency === MembershipTierPlanFrequency.MONTH)!;
      const annualPlan = tier.membershipTierPlans.find((plan) => plan.frequency === MembershipTierPlanFrequency.YEAR);
      const diff = (tier.isFixedTier && annualPlan) ? (monthlyPlan.price * 12 - annualPlan.price) : 0;

      // return highest value of savings or diff but with only 2 decimal places
      return parseFloat(Math.max(savings, diff).toFixed(2));
    }, 0);
  });
</script>

<template>
  <Slider v-if="!mobile" :loading="false" :resetPageTrigger="resetPageTrigger">
    <template #title>
      <div v-if="showSubTitle" class="t-title-2 t-bold title">
        {{ $t('modal.membership.sub-header') }}
      </div>
      <div v-if="showAnnualToggle" class="annual-toggle">
        <div class="toggle">
          <span :class="{selected: !showAnnual}" class="uppercase"  @click="showAnnual = false">{{ $t('common.monthly') }}</span>
          <v-switch
            v-model="showAnnual"
            hide-details
            inset
            density="compact"
            @change="toggleFrequency(showAnnual)"
          ></v-switch>
          <span :class="{selected: showAnnual}" class="uppercase" @click="showAnnual = true">{{ $t('common.annual') }}</span>
        </div>
        <v-chip size="small" color="success-200" v-if="annualMaxSavings > 0">
          {{ $t('modal.membership.tier.list.annual.subtitle', { price: annualMaxSavings }) }}
        </v-chip>
      </div>
    </template>
    <MembershipTierDetails
      v-for="(tier, index) in visibleTiers"
      :key="`${tier.id}-tier`"
      :membership-tier="tier"
      :membership-tier-plan="tier.membershipTierPlans.find((plan) => plan.frequency === (showAnnual ? MembershipTierPlanFrequency.YEAR : MembershipTierPlanFrequency.MONTH))"
      :previous-tier-name="visibleTiers[index - 1]?.labels[sessionStore.language]"
    />
  </Slider>
  <template v-else>
    <div v-if="showAnnualToggle" class="annual-toggle">
      <div class="toggle">
        <span :class="{selected: !showAnnual}" class="uppercase" @click="showAnnual = false">{{ $t('common.monthly') }}</span>
        <v-switch
          v-model="showAnnual"
          hide-details
          inset
          @change="toggleFrequency(showAnnual)"
          density="compact"
        ></v-switch>
        <span :class="{selected: showAnnual}" class="uppercase" @click="showAnnual = true">{{ $t('common.annual') }}</span>
      </div>
      <v-chip size="small" color="success-200" v-if="annualMaxSavings > 0">
        {{ $t('modal.membership.tier.list.annual.subtitle', { price: annualMaxSavings }) }}
      </v-chip>
    </div>
    <MembershipTierDetails
      v-for="(tier, index) in visibleTiers"
      :key="`${tier.id}-tier`"
      :membership-tier="tier"
      :membership-tier-plan="tier.membershipTierPlans.find((plan) => plan.frequency === (showAnnual ? MembershipTierPlanFrequency.YEAR : MembershipTierPlanFrequency.MONTH))"
      :previous-tier-name="visibleTiers[index - 1]?.labels[sessionStore.language]"
    />
  </template>
  <div class="mt-2 notice" v-if="firstRoundUpTier">
    {{
      $t('modal.membership.notice', { plan: firstRoundUpTier.labels[ sessionStore.language ] })
    }}
  </div>
</template>

<style lang="scss" scoped>
  .uppercase::first-letter {
    text-transform: uppercase;
  }

  .annual-toggle {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;

    margin-top: 1rem;

    .toggle {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: getFontSize('body-2');

      span.selected {
        font-weight: getFontWeight('bold');
      }

      .v-switch {
        flex: unset;
      }
    }

    :deep(.v-chip) {
      font-weight: getFontWeight('bold');
      background-color: inherit;
    }
  }

  .notice {
    font-size: getFontSize('body-2');
    color: rgb(var(--v-theme-neutral-400));
  }

  @media (max-width: breakpoint(md) - 1px) {
    .annual-toggle {
      margin: 0.5rem 0 1rem 0;
    }

    .notice {
      font-size: getFontSize('caption');
      line-height: 1rem;
    }
  }
</style>
